import type { Character, DialogueAction, Hotspot, Location, Manifest } from "../manifest/schema.js";
import { NavGraph, type Edge } from "../nav/graph.js";
import { Navigator } from "../nav/navigator.js";
import { formatHash, parseHash } from "../nav/route.js";
import { TransitionPlayer } from "../player/transitionPlayer.js";
import { ScriptedDialogueProvider } from "../dialogue/scripted.js";
import type { DialogueProvider } from "../dialogue/provider.js";
import { Basket, type Storage } from "../shop/basket.js";
import { h } from "./dom.js";
import { Stage } from "./stage.js";
import { ChatPanel } from "./chatPanel.js";
import { MockBackend, WorkerBackend } from "../ai/backend.js";
import type { AiConfig } from "../ai/config.js";
import { buildGrounding } from "../ai/grounding.js";
import { BrowserTts, SilentTts, WorkerTts } from "../ai/voice.js";
import { configuratorSchema, type Configurator } from "../configurator/schema.js";
import { configuratorContent } from "../configurator/panel.js";
import { telescopeContent } from "./telescope.js";
import { retailerConfigContent } from "../retailer/panel.js";
import { DialoguePanel, Modal, basketContent, directoryContent, menuContent, productContent, type PanelDeps } from "./panels.js";

export interface AppOptions {
  manifest: Manifest;
  mediaBase: string;
  reducedMotion: boolean;
  supportsWebm: boolean;
  storage?: Storage;
  dialogue?: DialogueProvider;
  /** AI chat for selected characters. null/undefined = scripted only. */
  ai?: AiConfig | null;
  /** Reads a JSON content file relative to `content/` (used for car configurators). */
  loadContent?: (path: string) => Promise<unknown>;
}

/** Wires manifest, navigation, player, stage and panels together. */
export class App {
  readonly root = h("div", { class: "app" });
  private readonly manifest: Manifest;
  private readonly graph: NavGraph;
  private nav: Navigator;
  private readonly locations: Map<string, Location>;
  private readonly basket: Basket;
  private readonly stage: Stage;
  private readonly player: TransitionPlayer;
  private readonly modal = new Modal("panel");
  private readonly dialogue: DialoguePanel;
  private readonly live = h("div", { class: "sr-only", role: "status", "aria-live": "polite" });
  private readonly title = h("div", { class: "hud-title" });
  private readonly toast = h("div", { class: "toast", role: "status", hidden: true });
  private readonly skipBtn = h("button", { class: "btn skip", hidden: true, "data-testid": "skip", onclick: () => this.skip() }, "Skip walk");
  private readonly backBtn = h("button", { class: "btn", "data-testid": "back", onclick: () => void this.back() }, "Back");
  private readonly basketBtn = h("button", { class: "btn", "data-testid": "basket-button", onclick: () => this.openBasket() }, "Basket (0)");
  private readonly deps: PanelDeps;
  private readonly chats = new Map<string, ChatPanel>();
  private readonly configurators = new Map<string, Promise<Configurator>>();
  private readonly notice = h("p", { class: "location-notice", "data-testid": "location-notice", hidden: true });
  private travelling = false;
  private stageLocation: string;

  constructor(private readonly options: AppOptions) {
    const m = (this.manifest = options.manifest);
    this.graph = new NavGraph(m);
    this.locations = new Map(m.locations.map((l) => [l.id, l]));
    this.nav = new Navigator(m.mall.startLocationId);
    this.stageLocation = m.mall.startLocationId;
    this.basket = new Basket(
      new Map(m.products.map((p) => [p.id, p])),
      options.storage,
      m.shops.flatMap((s) => (s.promos ?? []).map((p) => ({ ...p, shopId: s.id, shopName: s.name })))
    );
    const mediaUrl = (rel: string) => `${options.mediaBase}${rel}`;
    this.deps = { manifest: m, basket: this.basket, mediaUrl, travelToShop: (id) => void this.travelToShop(id), announce: (s) => this.announce(s) };
    this.stage = new Stage(mediaUrl, {
      onHotspot: (hs) => this.onHotspot(hs),
      onCharacter: (c) => void this.onCharacter(c),
      onBackgroundClick: () => this.skip()
    }, { reducedMotion: options.reducedMotion, supportsWebm: options.supportsWebm });
    this.player = new TransitionPlayer(this.stage.videoLayer, { reducedMotion: options.reducedMotion, supportsWebm: options.supportsWebm, mediaUrl });
    this.dialogue = new DialoguePanel(options.dialogue ?? new ScriptedDialogueProvider(), m, this.basket, () => this.nav.current, (a) => this.onAction(a));

    // Every character with an `ai` block gets the free-text chat (archived characters are already gone from the manifest).
    for (const c of m.characters) {
      const ai = options.ai;
      if (!ai || !c.ai) continue;
      const g = buildGrounding(m, c.id);
      const backend = ai.workerUrl ? new WorkerBackend(ai.workerUrl, g) : new MockBackend(g);
      const female = /\b(woman|she|her|hostess|lady|girl)\b/i.test(c.persona.summary);
      const tts = ai.tts === "off" ? new SilentTts() : ai.tts === "worker" && ai.workerUrl ? new WorkerTts(ai.workerUrl, c.id, new BrowserTts(undefined, female)) : new BrowserTts(undefined, female);
      this.chats.set(c.id, new ChatPanel(backend, tts, {
        onSpeaking: (on) => this.stage.setTalking(c, on),
        // Never trust the model's target: it must be one of this character's guide places and still exist (not archived).
        onTravel: (to) => {
          if (g.guides.some((x) => x.locationId === to && !x.archived) && this.locations.has(to)) void this.travelTo(to);
        },
        onAction: (a) => {
          if (a.type === "goToLocation" || a.type === "goToShop") this.chats.get(c.id)?.close();
          this.onAction(a);
        },
        announce: (s) => this.announce(s)
      }));
    }

    const hud = h(
      "header",
      { class: "hud" },
      h("div", { class: "hud-brand" }, h("strong", {}, m.mall.name), h("span", { class: "muted" }, ` ${m.mall.tagline}`)),
      this.title,
      h("nav", { class: "hud-actions", "aria-label": "Mall" }, this.backBtn, h("button", { class: "btn", "data-testid": "directory-button", onclick: () => this.openDirectory() }, "Directory"), this.basketBtn)
    );
    this.root.append(hud, h("main", { class: "stage-wrap" }, this.stage.root, this.notice, this.dialogue.root, ...[...this.chats.values()].map((c) => c.root), this.toast, this.skipBtn), this.modal.root, this.live);
    this.basket.onChange(() => (this.basketBtn.textContent = `Basket (${this.basket.count})`));
    this.basketBtn.textContent = `Basket (${this.basket.count})`;
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.travelling) this.skip();
    });
    window.addEventListener("popstate", () => void this.fromHash());
  }

  /** Mount and show the start (or deep-linked) location. */
  async start(): Promise<void> {
    const route = parseHash(location.hash);
    const startId = route?.kind === "location" && this.locations.has(route.id) ? route.id : this.manifest.mall.startLocationId;
    this.nav = new Navigator(startId);
    await this.showLocation(startId);
    if (route?.kind === "shop") void this.travelToShop(route.id);
  }

  // ----------------------------------------------------------- navigation
  private async showLocation(id: string): Promise<void> {
    const loc = this.locations.get(id);
    if (!loc) return;
    this.stageLocation = id;
    await this.stage.setStill(loc);
    this.stage.showInteractive(loc, this.manifest.characters.filter((c) => c.locationId === id));
    const district = this.manifest.districts.find((d) => d.id === loc.districtId);
    this.title.textContent = `${loc.name}${district ? ` · ${district.name}` : ""}`;
    this.title.dataset.location = id;
    this.notice.hidden = !loc.notice;
    this.notice.textContent = loc.notice ?? "";
    this.backBtn.disabled = !this.nav.canGoBack;
    this.announce(`${loc.name}. ${loc.alt}`);
    this.preloadNeighbours(id);
    // Fetch the (tiny) configurator file ahead of the tap so the panel opens instantly.
    for (const hs of loc.hotspots) if (hs.kind === "configurator") void this.configurator(hs.src).catch(() => undefined);
  }

  private preloadNeighbours(id: string): void {
    const edges = this.graph.edgesFrom(id);
    this.player.preload(edges);
    for (const e of edges) {
      const l = this.locations.get(e.to);
      if (l) new Image().src = `${this.options.mediaBase}${l.still}.jpg`;
    }
  }

  private async travel(route: Edge[], final: string, record: "push" | "back" | "none"): Promise<void> {
    if (this.travelling || route.length === 0) return;
    this.travelling = true;
    this.dialogue.close();
    for (const c of this.chats.values()) c.close();
    this.stage.hideInteractive();
    this.skipBtn.hidden = this.options.reducedMotion;
    try {
      for (const [i, edge] of route.entries()) {
        const dest = this.locations.get(edge.to);
        if (!dest) break;
        this.toast.hidden = false;
        this.toast.textContent = route.length > 1 ? `${edge.label} (${i + 1} of ${route.length})` : edge.label;
        this.announce(edge.label);
        const poster = this.locations.get(edge.from);
        await this.player.play(edge, poster ? `${this.options.mediaBase}${poster.still}.jpg` : undefined);
        await this.stage.setStill(dest); // the clip's last frame equals this still
        this.stageLocation = dest.id;
        this.player.release();
        this.preloadNeighbours(dest.id);
      }
    } finally {
      this.travelling = false;
      this.skipBtn.hidden = true;
      this.toast.hidden = true;
    }
    if (record === "push") this.nav.arrive(final);
    else if (record === "back") this.nav.back();
    const hash = formatHash({ kind: "location", id: final });
    if (location.hash !== hash) history.pushState(null, "", hash);
    await this.showLocation(final);
  }

  /** Walk to any location along the cheapest route. */
  async travelTo(target: string, record: "push" | "back" | "none" = "push"): Promise<void> {
    const route = this.graph.path(this.stageLocation, target);
    if (!route) return this.say(`There is no way to get there from here.`);
    if (route.length === 0) return;
    await this.travel(route, target, record);
  }

  /** "Take me to shop X". */
  async travelToShop(shopId: string): Promise<void> {
    const route = this.graph.pathToShop(this.stageLocation, shopId);
    const shop = this.manifest.shops.find((s) => s.id === shopId);
    if (!route || !shop) return this.say("I could not find that shop.");
    if (route.length === 0) return this.say(`You are already at ${shop.name}.`);
    await this.travel(route, shop.entryLocationId, "push");
  }

  private async back(): Promise<void> {
    const prev = this.nav.peekBack();
    if (prev === undefined || this.travelling) return;
    await this.travelTo(prev, "back");
  }

  private async fromHash(): Promise<void> {
    const route = parseHash(location.hash);
    if (!route || this.travelling) return;
    if (route.kind === "shop") await this.travelToShop(route.id);
    else if (this.locations.has(route.id) && route.id !== this.nav.current) await this.travelTo(route.id, "push");
  }

  private skip(): void {
    this.player.skip();
  }

  // ------------------------------------------------------------ interaction
  private onHotspot(hs: Hotspot): void {
    switch (hs.kind) {
      case "navigate":
        void this.travelTo(hs.to);
        break;
      case "product":
        this.openProduct(hs.productId);
        break;
      case "directory":
        this.openDirectory();
        break;
      case "checkout":
        this.openBasket();
        break;
      case "info":
        this.say(hs.text);
        break;
      case "configurator":
        void this.openConfigurator(hs.src);
        break;
      case "clue":
        this.options.storage?.setItem(`red.clue.${hs.id}.v1`, "1");
        this.modal.open(
          h(
            "div",
            { class: "clue", "data-testid": "clue" },
            h("h2", { "data-autofocus": true, tabindex: -1 }, hs.title),
            h("p", {}, hs.text),
            h("p", { class: "clue-code" }, "Code: ", h("strong", { "data-testid": "clue-code" }, hs.code)),
            h("p", { class: "muted small" }, `Use it in the basket on priced items from ${this.manifest.shops.find((x) => x.id === hs.shopId)?.name ?? "this shop"}. Demo code, no real discount.`)
          ),
          hs.title
        );
        break;
      case "retailer-config":
        this.modal.open(retailerConfigContent({ storage: this.options.storage, announce: (t) => this.announce(t), close: () => this.modal.close() }), hs.title);
        break;
      case "telescope":
        this.modal.open(
          telescopeContent(hs, {
            mediaUrl: this.deps.mediaUrl,
            announce: (s) => this.announce(s),
            reducedMotion: this.options.reducedMotion,
            shopName: (id) => this.manifest.shops.find((s) => s.id === id)?.name ?? "the shop",
            onFound: () => this.options.storage?.setItem("red.telescope.found.v1", "1"),
            goToShop: (id) => {
              this.modal.close();
              void this.travelToShop(id);
            },
            close: () => this.modal.close()
          }),
          hs.title
        );
        break;
      case "menu":
        this.modal.open(
          menuContent(
            hs.title,
            hs.items.filter((i) => this.locations.has(i.to)).map((i) => ({ ...i, current: i.to === this.stageLocation })),
            (to) => {
              this.modal.close();
              void this.travelTo(to);
            }
          ),
          hs.title
        );
        break;
    }
  }

  private async onCharacter(c: Character): Promise<void> {
    const chat = this.chats.get(c.id);
    if (chat) {
      this.dialogue.close();
      const start = c.script.nodes[c.script.start];
      const g = buildGrounding(this.manifest, c.id);
      chat.open(c, g.rules.greeting, start?.options.map((o) => ({ text: o.text, ...(o.action ? { action: o.action } : {}) })) ?? []);
      return;
    }
    this.stage.talk(c);
    await this.dialogue.open(c.id);
  }

  private onAction(a: DialogueAction): void {
    switch (a.type) {
      case "showProduct":
        this.openProduct(a.productId);
        break;
      case "addToBasket":
        this.basket.add(a.productId);
        break;
      case "goToLocation":
        this.dialogue.close();
        void this.travelTo(a.locationId);
        break;
      case "goToShop":
        this.dialogue.close();
        void this.travelToShop(a.shopId);
        break;
      case "openDirectory":
        this.openDirectory();
        break;
      case "openBasket":
        this.openBasket();
        break;
    }
  }

  private openDirectory(): void {
    this.modal.open(directoryContent(this.deps, () => this.modal.close()));
  }

  private openBasket(): void {
    this.modal.open(basketContent(this.deps, () => this.modal.close()));
  }

  private configurator(src: string): Promise<Configurator> {
    let p = this.configurators.get(src);
    if (!p) {
      const load = this.options.loadContent;
      p = load ? load(src).then((j) => configuratorSchema.parse(j)) : Promise.reject(new Error("configurator content is not available"));
      p.catch(() => this.configurators.delete(src));
      this.configurators.set(src, p);
    }
    return p;
  }

  private async openConfigurator(src: string): Promise<void> {
    try {
      const c = await this.configurator(src);
      this.modal.open(configuratorContent(c, { basket: this.basket, mediaUrl: this.deps.mediaUrl, openBasket: () => this.openBasket() }), c.title);
    } catch {
      this.say("The configurator could not be loaded. Please try again.");
    }
  }

  private openProduct(id: string): void {
    const p = this.manifest.products.find((x) => x.id === id);
    if (p) this.modal.open(productContent(p, this.deps));
  }

  private say(text: string): void {
    this.toast.hidden = false;
    this.toast.textContent = text;
    this.announce(text);
    setTimeout(() => {
      if (!this.travelling && this.toast.textContent === text) this.toast.hidden = true;
    }, 4000);
  }

  private announce(msg: string): void {
    this.live.textContent = msg;
  }
}
